/**
 * Hotels API endpoints
 *
 * Architecture notes:
 *
 * GATEWAY (`/hotels/v1/…` on the api-gateway host):
 *   Every endpoint backed by the hotels-service OpenAPI spec
 *   (`docs/openapi/cx-hotels-service.yaml`) is called directly through the
 *   api-gateway (`customer-api.mangovoice.com`) rather than the mango-api
 *   `/hospitality/…` proxy. The gateway forwards `/hotels/{proxy+}`
 *   unmodified, so the spec's `/hotels/v1/…` paths are the callable paths.
 *
 *   The gateway's Cognito authorizer accepts the same bearer access token as
 *   mango-api and injects the `X-MANGO-AUTHZ` header for the service.
 *
 *   Resources: pbxes, pbx rooms (list + bulk update), room detail,
 *   wake-up calls (list, create, bulk create, delete, bulk delete).
 *
 * MANGO-API (`/app/v1/…`):
 *   Endpoints with no hotels-service equivalent stay on mango-api:
 *   - CALLS — OpenSearch-backed call history (replaces legacy CDR endpoints)
 *   - ROOM_VOICEMAILS — queries local Voicemail table
 *   - ROOM_VOICEMAILS_CLEAR_ALL — soft-deletes local Voicemails
 *   - HOSPITALITY_EXTENSIONS — toggles Extension.hospitality flag locally
 *   - EXTENSION_DETAIL (hospitality toggle) — patches local Extension
 *   - EXTENSION_LINES — extension ↔ room mapping used to enrich the
 *     UUID-keyed hotels-service payloads with numeric ids/numbers/names
 *
 * Call History API (/app/v1/calls/)
 *
 * Uses OpenSearch — no CDR table (logger_call) dependency.
 *
 * Supported query parameters:
 *   - pbx_ids:       Comma-separated PBX IDs
 *   - company_ids:   Comma-separated Company IDs
 *   - directions:    Comma-separated: 'inbound', 'outbound', 'internal'
 *   - types:         Comma-separated: 'standard', 'fax'
 *   - statuses:      Comma-separated call statuses
 *   - started_after / started_before: ISO 8601 UTC datetime
 *   - ended_after / ended_before:     ISO 8601 UTC datetime
 *   - duration_in_seconds_gte / duration_in_seconds_lt: Duration range filters
 *   - is_missed:     'true' | 'false'
 *   - search:        Exact (case-insensitive) match on from.caller_id,
 *                    from.formatted, or to.formatted (handles phone formatting)
 *   - related_user_ids:   Comma-separated User IDs ("My Calls" filter)
 *   - relevant_phone_number_ids: Comma-separated PhoneNumber IDs
 *   - relevant_location_ids:     Comma-separated Location IDs
 *   - ordering:      Comma-separated fields: 'started_at', '-started_at',
 *                    'ended_at', '-ended_at', 'duration_in_seconds',
 *                    '-duration_in_seconds'
 *   - limit:         Page size (default 25)
 *   - offset:        Pagination offset
 *
 * Response shape (paginated):
 *   { count, next, previous, results: Call[] }
 *
 * Each Call in results:
 *   {
 *     id, company: {id, name}, pbx: {id, name},
 *     status, type, direction, is_missed,
 *     duration_in_seconds, started_at, ended_at,
 *     from: { caller_id, formatted, cnam_location,
 *             extension: {id, name, number} | null,
 *             phone_number: {id, name, number} | null,
 *             location: {id, name} | null,
 *             user: {id, name} | null },
 *     to:   { caller_id, formatted, cnam_location,
 *             extension: {id, name, number} | null,
 *             phone_number: {id, name, number} | null,
 *             location: {id, name} | null,
 *             user: {id, name} | null },
 *     voicemail_id, fax_id, transcript_id,
 *     transcription_status, call_sentiment,
 *     externally_transferred_at, created_at, updated_at
 *   }
 */
import type {
  paths as HotelsServicePaths,
  operations,
} from '@mangovoice/cx-hotels-service-client'

/**
 * Every hotels-service route template, straight from the generated OpenAPI
 * types. Using this as the builders' input type means a route that upstream
 * renames, moves, or drops stops compiling here — `scripts/sync-cx-specs.sh`
 * regenerates `paths` from the committed spec, so drift surfaces at build time
 * rather than as a 404 in the browser.
 */
type HotelsServiceRoute = keyof HotelsServicePaths

/** Substitute a spec route's path parameters. */
const gatewayUrl = (
  route: HotelsServiceRoute,
  params: Record<string, string> = {},
): string =>
  Object.entries(params).reduce(
    (acc, [name, value]) => acc.replace(`{${name}}`, encodeURIComponent(value)),
    route as string,
  )

/**
 * hotels-service endpoints, called through the api-gateway.
 * PBX-scoped variants are preferred over the flat ones so the service can
 * authorize against the PBX in the path.
 */
export const HOTELS_GATEWAY_ENDPOINTS = {
  /** GET (detail) / PATCH (reset schedule, timezone, name) */
  PBX_DETAIL: (pbxUuid: string) =>
    gatewayUrl('/hotels/v1/pbxes/{pbx_uuid}', { pbx_uuid: pbxUuid }),
  /** GET (list rooms) / PATCH (bulk update every room on the PBX) */
  PBX_ROOMS: (pbxUuid: string) =>
    gatewayUrl('/hotels/v1/pbxes/{pbx_uuid}/rooms', { pbx_uuid: pbxUuid }),
  /** GET / PATCH a single room, keyed by extension UUID */
  ROOM_DETAIL: (extensionUuid: string) =>
    gatewayUrl('/hotels/v1/rooms/{extension_uuid}', {
      extension_uuid: extensionUuid,
    }),
  /** GET (list) / POST (create one or many) / DELETE (by extension_uuid) */
  PBX_WAKEUP_CALLS: (pbxUuid: string) =>
    gatewayUrl('/hotels/v1/pbxes/{pbx_uuid}/wakeup-calls', {
      pbx_uuid: pbxUuid,
    }),
  /** DELETE by required `legacy_id` query param(s) */
  WAKEUP_CALLS: gatewayUrl('/hotels/v1/wakeup-calls'),
  /** GET / DELETE a single wake-up call by its own UUID */
  WAKEUP_CALL_DETAIL: (uuid: string) =>
    gatewayUrl('/hotels/v1/wakeup-calls/{uuid}', { uuid }),
} as const

/**
 * Query-string types for the endpoints the client filters on. Sourced from the
 * generated `operations` so a renamed or dropped parameter fails to compile —
 * the client builds these query strings by hand, and a silently-ignored filter
 * (e.g. an unscoped wake-up call list) is worse than a hard error.
 */
export type ListPbxRoomsQuery = NonNullable<
  operations['hotelsV1ListPbxRooms']['parameters']['query']
>
export type ListPbxWakeupCallsQuery = NonNullable<
  operations['hotelsV1ListPbxWakeUpCalls']['parameters']['query']
>
export type DeletePbxWakeupCallsQuery = NonNullable<
  operations['hotelsV1DeletePbxWakeUpCalls']['parameters']['query']
>

/**
 * Endpoints still served by mango-api — either because there is no
 * hotels-service equivalent (calls, voicemails, extension flags) or because
 * the data lives only in mango-api (extension lines).
 */
export const NON_PROXIED_ENDPOINTS = {
  CALLS: '/app/v1/calls/',
  /**
   * Server-side emergency-call history. mango-api filters by region-specific
   * emergency `destination_number` in MySQL and caps to the last 7 days / 10
   * rows, so no client-side number matching or paging workaround is needed.
   */
  CALL_HISTORY_EMERGENCY: '/app/v1/call-history/emergency/',
  ROOM_VOICEMAILS: '/app/v1/voicemails/hospitality/',
  ROOM_VOICEMAILS_CLEAR_ALL: '/app/v1/voicemails/hospitality/clear/',
  VOICEMAIL_DETAIL: (id: number) => `/app/v1/voicemails/${id}/`,
  EXTENSION_DETAIL: (extensionId: number) =>
    `/app/v1/extensions/${extensionId}/`,
  HOSPITALITY_EXTENSIONS: '/app/v1/extensions/bulk-hospitality/',
  /**
   * No hotels-service equivalent. Supplies the numeric id / number / name for
   * each extension so UUID-keyed rooms and wake-up calls can be enriched.
   * The `/hospitality/v1/` variant is staff-only, so customers must use this one.
   */
  EXTENSION_LINES: (pbxId: number) => `/app/v1/pbxes/${pbxId}/extension-lines/`,
} as const
